import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { getUtm } from "@/lib/utm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldCheck,
  Users,
  Wallet,
  MegaphoneIcon,
  Handshake,
  ArrowRight,
  Sparkles,
  ClipboardCheck,
  PlayCircle,
} from "lucide-react";

const FOUNDER_VIDEO_URL = process.env.REACT_APP_FOUNDER_VIDEO_URL || "";

// Convert a plain YouTube URL to an embed URL. Returns "" if not a YT link.
function toYouTubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    return url; // pass through Vimeo/Loom/etc.
  } catch {
    return "";
  }
}

function FounderVideo() {
  const embed = toYouTubeEmbed(FOUNDER_VIDEO_URL);
  if (embed) {
    return (
      <div className="rounded-2xl overflow-hidden bg-black shadow-[0_20px_60px_rgba(0,0,0,0.25)] aspect-video">
        <iframe
          src={embed}
          title="Our First Realty — founders' walkthrough"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          data-testid="founder-video-iframe"
        />
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl overflow-hidden relative aspect-video bg-forest shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
      data-testid="founder-video-placeholder"
    >
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 30%, #D4AF37 0, transparent 45%), radial-gradient(circle at 75% 70%, #D4AF37 0, transparent 40%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <div className="w-20 h-20 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center backdrop-blur-sm">
          <PlayCircle className="w-10 h-10 text-gold" />
        </div>
        <div className="mt-6 text-[11px] uppercase tracking-[0.28em] text-gold font-bold">Video coming soon</div>
        <h3 className="mt-3 font-heading font-extrabold text-2xl lg:text-3xl text-white tracking-tight max-w-lg">
          A 45-second founder walkthrough — dropping this week.
        </h3>
        <p className="mt-3 text-sm text-white/70 max-w-md">
          In the meantime, our founders will personally jump on a 15-minute call. Skip the video, book the call.
        </p>
        <a
          href="#list-project"
          data-testid="video-cta-book-call"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-forest font-bold px-6 h-11"
        >
          Book the call <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

const VALUE_PROPS = [
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "Bank-approved financing earns buyer trust",
    desc: "Every project is site-visited and bank-approved before listing. That backing gets your project onto every parent's WhatsApp before they even visit.",
  },
  {
    icon: <MegaphoneIcon className="w-5 h-5" />,
    title: "Done-for-you marketing",
    desc: "Instagram reels, Google Ads, WhatsApp broadcasts, portal listings — we run the pipeline; you get scheduled site visits.",
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: "Buyer-side loan concierge",
    desc: "Tie-ups with multiple leading banks & NBFCs. We pre-approve buyers before they walk in, so registrations don't slip.",
  },
  {
    icon: <Wallet className="w-5 h-5" />,
    title: "Only pay on closed bookings",
    desc: "Zero listing fee. You only pay when a unit registers — no retainers, no minimums, no lock-ins.",
  },
];

const VERIFICATION_STEPS = [
  { title: "RERA registration checked, when it applies", note: "some early-stage / small builders aren't RERA-registered yet; those are still verified through bank APF sanctioning and our own site inspection" },
  { title: "Bank-approved for home loans", note: "banks perform their own diligence before financing" },
  { title: "Site inspection by our field team", note: "photo + video, uploaded to your listing" },
  { title: "Bank loan-eligibility review", note: "so buyers finance on day one" },
  { title: "Signed listing agreement", note: "commission agreed per project, closed-only, no lock-in" },
];

const HOW = [
  { n: "01", t: "Introduce your project", d: "15-minute call — we review RERA #, unit inventory, current pricing, and your goal timeline." },
  { n: "02", t: "We verify + list", d: "3–5 working days. On approval, your project goes live with a public landing page and shareable link." },
  { n: "03", t: "We run marketing + book visits", d: "Ads, WhatsApp broadcasts, tele-callers. Every lead lands in our CRM with a UTM trail." },
  { n: "04", t: "We close + register", d: "Loan tie-ups, negotiation, token, agreement, registration — buyer walks in, keys walk out." },
];

export default function ForBuilders() {
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    api.get("/builder-feedback/latest").then(r => setFeedback(r.data?.item || null)).catch(() => setFeedback(null));
  }, []);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_name: "",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        project_name: form.project_name || "For-Builders enquiry",
        message: `Location: ${form.location || "—"}\n${form.message || ""}`.trim(),
        source: "builder-landing",
        ...getUtm(),
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream" data-testid="for-builders-page">
      {/* HERO */}
      <section className="bg-forest text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #D4AF37 0, transparent 40%), radial-gradient(circle at 80% 60%, #D4AF37 0, transparent 35%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs uppercase tracking-[0.24em] text-gold font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> For Hyderabad builders
            </div>
            <h1 className="mt-6 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              You build.
              <br />
              <span className="text-gold">We sell.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
              Small &amp; unorganized builders lose 30–40% of margin to unbranded brokers, thin ads, and stalled loans.
              Our First Realty is the trust-first sales arm your project deserves — verified listing, marketing, buyer
              concierge, and closing, all on a transparent, closed-only success fee.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#list-project"
                data-testid="hero-cta-list"
                className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-forest font-bold px-6 h-12"
              >
                List your project <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-gold text-white font-medium px-6 h-12"
              >
                How it works
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-heading font-extrabold text-3xl text-gold">₹0</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">Upfront · Closed-only fee</div>
              </div>
              <div>
                <div className="font-heading font-extrabold text-3xl text-gold">0₹</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">Listing charge</div>
              </div>
              <div>
                <div className="font-heading font-extrabold text-3xl text-gold">3–5d</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">To go live</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-6">
            <div className="rounded-2xl bg-white text-forest p-6 lg:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">Talk to founders</div>
              <div className="font-heading font-extrabold text-2xl mt-2">Get a listing review — free</div>
              <p className="text-sm text-muted-foreground mt-1">
                15-minute call. We tell you honestly if your project fits our verification bar.
              </p>
              <a
                href="#list-project"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest hover:bg-forest-hover text-white h-11 font-medium"
              >
                Jump to form <ArrowRight className="w-4 h-4" />
              </a>
              <div className="mt-6 text-[13px] leading-relaxed text-muted-foreground border-t border-border pt-5" data-testid="builder-testimonial">
                {feedback ? (
                  <>
                    <div>&ldquo;{feedback.feedback_quote}&rdquo;</div>
                    <div className="mt-2 text-forest font-semibold text-xs">
                      — {feedback.builder_name}, {feedback.company_name}
                    </div>
                  </>
                ) : (
                  <div className="italic text-muted-foreground">Builder success stories coming soon.</div>
                )}
                <button
                  type="button"
                  onClick={() => setShowFeedback(true)}
                  data-testid="open-feedback-form"
                  className="mt-3 text-xs text-gold-hover hover:text-forest underline underline-offset-4"
                >
                  Are you a partnered builder? Share your feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER VIDEO */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 -mt-10 relative z-10" data-testid="founder-video-section">
        <FounderVideo />
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">Why builders switch</div>
        <h2 className="mt-3 font-heading font-extrabold text-3xl lg:text-4xl text-forest tracking-tight max-w-3xl">
          You are great at building.
          <br />
          Great sales is a different job.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {VALUE_PROPS.map((v) => (
            <div
              key={v.title}
              className="rounded-2xl bg-white border border-border p-6 hover:border-gold/60 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-forest/10 text-forest flex items-center justify-center">
                {v.icon}
              </div>
              <div className="mt-4 font-heading font-bold text-lg text-forest">{v.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">How it works</div>
          <h2 className="mt-3 font-heading font-extrabold text-3xl lg:text-4xl tracking-tight max-w-2xl">
            From your first call to a signed registration.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="font-heading font-extrabold text-2xl text-gold">{s.n}</div>
                <div className="mt-3 font-heading font-bold text-lg">{s.t}</div>
                <p className="text-sm text-white/70 mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERIFICATION CHECKLIST */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">Our verification bar</div>
          <h2 className="mt-3 font-heading font-extrabold text-3xl lg:text-4xl text-forest tracking-tight">
            Five checks stand between a project and a buyer&apos;s parents.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            We don&apos;t list what we won&apos;t personally recommend.
          </p>
        </div>
        <div className="lg:col-span-7">
          <ol className="space-y-3">
            {VERIFICATION_STEPS.map((s, i) => (
              <li
                key={s.title}
                className="flex gap-4 rounded-xl border border-border bg-white p-5 hover:border-gold/60 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-forest text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div className="font-heading font-bold text-forest">{s.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.note}</div>
                </div>
                <ClipboardCheck className="w-5 h-5 text-gold ml-auto shrink-0" />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* COMMISSION MODEL */}
      <section className="bg-cream border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">Our commercials</div>
            <h2 className="mt-3 font-heading font-extrabold text-3xl lg:text-4xl text-forest tracking-tight">
              Zero upfront.
              <br />
              You pay only when a unit registers.
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-forest">
              <li className="flex items-center gap-2">
                <Handshake className="w-4 h-4 text-gold" /> No listing fee, no retainer, no monthly minimum.
              </li>
              <li className="flex items-center gap-2">
                <Handshake className="w-4 h-4 text-gold" /> We don&apos;t get paid unless you do — same finish line, same incentive.
              </li>
              <li className="flex items-center gap-2">
                <Handshake className="w-4 h-4 text-gold" /> Cancel anytime with 30 days notice — no penalty.
              </li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white border border-border p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Compare</div>
            <table className="w-full mt-4 text-sm">
              <thead className="text-muted-foreground text-xs">
                <tr>
                  <th className="text-left font-medium py-2">Channel</th>
                  <th className="text-right font-medium py-2">You pay</th>
                  <th className="text-right font-medium py-2">Trust signal</th>
                </tr>
              </thead>
              <tbody className="text-forest">
                <tr className="border-t border-border">
                  <td className="py-3">Broker network</td>
                  <td className="py-3 text-right">2–4% + upfront</td>
                  <td className="py-3 text-right text-muted-foreground">Low</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-3">Portal ads</td>
                  <td className="py-3 text-right">₹40k–₹1.5L / month</td>
                  <td className="py-3 text-right text-muted-foreground">Low</td>
                </tr>
                <tr className="border-t border-border bg-gold/10">
                  <td className="py-3 font-bold">Our First Realty</td>
                  <td className="py-3 text-right font-bold">Pay only on close</td>
                  <td className="py-3 text-right font-bold text-forest">Bank-approved</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA FORM */}
      <section id="list-project" className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] text-gold font-bold">Start the review</div>
          <h2 className="mt-3 font-heading font-extrabold text-3xl lg:text-4xl text-forest tracking-tight">
            Tell us about your project.
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            One of the founders will call you back within one business day. If we can&apos;t help you, we&apos;ll tell you honestly
            — no fluff.
          </p>
        </div>

        {done ? (
          <div
            data-testid="for-builders-success"
            className="mt-10 rounded-2xl border border-forest/20 bg-forest/5 p-8 text-forest text-center"
          >
            <div className="font-heading font-extrabold text-2xl">Thanks — we&apos;ve got it.</div>
            <p className="mt-2 text-sm">
              A founder will call {form.phone && <span className="font-semibold">{form.phone}</span>} within one business
              day. Meanwhile, feel free to check out our{" "}
              <Link to="/projects" className="text-gold-hover font-semibold underline">
                verified projects
              </Link>{" "}
              to see how listings look.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="for-builders-form"
            className="mt-10 rounded-2xl bg-white border border-border p-6 lg:p-8 grid md:grid-cols-2 gap-4"
          >
            <div>
              <Label htmlFor="fb-name" className="text-xs">
                Your name *
              </Label>
              <Input
                id="fb-name"
                data-testid="fb-name"
                value={form.name}
                onChange={(e) => upd("name", e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="fb-phone" className="text-xs">
                Phone / WhatsApp *
              </Label>
              <Input
                id="fb-phone"
                data-testid="fb-phone"
                value={form.phone}
                onChange={(e) => upd("phone", e.target.value)}
                placeholder="+91 …"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="fb-email" className="text-xs">
                Email (optional)
              </Label>
              <Input
                id="fb-email"
                type="email"
                data-testid="fb-email"
                value={form.email}
                onChange={(e) => upd("email", e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="fb-project" className="text-xs">
                Project name
              </Label>
              <Input
                id="fb-project"
                data-testid="fb-project"
                value={form.project_name}
                onChange={(e) => upd("project_name", e.target.value)}
                placeholder="e.g. Kompally Villa"
              />
            </div>
            <div>
              <Label htmlFor="fb-loc" className="text-xs">
                Location
              </Label>
              <Input
                id="fb-loc"
                data-testid="fb-location"
                value={form.location}
                onChange={(e) => upd("location", e.target.value)}
                placeholder="Neighborhood"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="fb-msg" className="text-xs">
                Anything we should know (unit count, RERA #, timeline)
              </Label>
              <Textarea
                id="fb-msg"
                rows={4}
                data-testid="fb-message"
                value={form.message}
                onChange={(e) => upd("message", e.target.value)}
                placeholder="e.g. 26 units, RERA registered, aiming to close in 90 days"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
              <p className="text-[11px] text-muted-foreground max-w-md">
                By submitting you agree to receive calls / WhatsApp from Our First Realty. We never share your details.
              </p>
              <Button
                type="submit"
                disabled={loading}
                data-testid="fb-submit"
                className="bg-forest hover:bg-forest-hover text-white h-12 px-8 rounded-full"
              >
                {loading ? "Sending…" : "Request a review"}
              </Button>
            </div>
          </form>
        )}
      </section>

      {showFeedback && <BuilderFeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );
}

function BuilderFeedbackModal({ onClose }) {
  const [f, setF] = useState({ builder_name: "", company_name: "", feedback_quote: "", rating: 5 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.builder_name.trim() || !f.company_name.trim() || !f.feedback_quote.trim()) {
      toast.error("All fields are required"); return;
    }
    setSaving(true);
    try {
      const r = await api.post("/builder-feedback", f);
      toast.success(r.data?.message || "Thanks!");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="feedback-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
        <div className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Builder feedback</div>
        <h3 className="font-heading text-2xl font-bold text-forest mt-1">Share your experience</h3>
        <p className="text-xs text-muted-foreground mt-1">We review every submission before it goes live.</p>
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs">Your name</Label>
            <Input value={f.builder_name} onChange={e => setF({...f, builder_name: e.target.value})} data-testid="fb-b-name" />
          </div>
          <div>
            <Label className="text-xs">Company</Label>
            <Input value={f.company_name} onChange={e => setF({...f, company_name: e.target.value})} data-testid="fb-b-company" />
          </div>
          <div>
            <Label className="text-xs">Your feedback</Label>
            <Textarea rows={4} value={f.feedback_quote} onChange={e => setF({...f, feedback_quote: e.target.value})} data-testid="fb-b-quote" />
          </div>
          <div>
            <Label className="text-xs">Rating (1-5)</Label>
            <Input type="number" min={1} max={5} value={f.rating} onChange={e => setF({...f, rating: parseInt(e.target.value)||5})} data-testid="fb-b-rating" />
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <Button type="button" onClick={onClose} variant="outline">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-forest hover:bg-forest-hover text-white" data-testid="fb-b-submit">
            {saving ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </form>
    </div>
  );
}
